import { MobileOfferAssignment, MobileOfferAssignmentStatus } from '@numeo/types'
import { History, RefreshCw } from 'lucide-react'
import { useCallback, useEffect, useRef, useState } from 'react'
import { mobileOfferError, mobileOffersApi } from './api'
import { CustomerActionDialog } from './CustomerActionDialog'
import { CustomerAssignmentResult } from './CustomerAssignmentForm'
import { CustomerReassignmentDialog } from './CustomerReassignmentDialog'
import { findCustomerAssignment } from './customer-assignment-reconciliation'
import { customerOfferDate, customerOfferSecondaryClass, customerPlanName, customerStoreName } from './customer-offer-utils'
import { OfferButton, OfferNotice, OfferSection } from './ui'
import { canReassignOffer, hasPendingReassignment } from './reassignmentState'

interface CustomerAssignmentHistoryProps {
    userId: string
    refreshToken: number
    onStatusChanged: (record: MobileOfferAssignment) => void
    onReassigned: (result: CustomerAssignmentResult) => void
}

export function CustomerAssignmentHistory({ userId, refreshToken, onStatusChanged, onReassigned }: CustomerAssignmentHistoryProps) {
    const [records, setRecords] = useState<MobileOfferAssignment[]>([])
    const [nextCursor, setNextCursor] = useState<string | null>(null)
    const [loading, setLoading] = useState(true)
    const [error, setError] = useState('')
    const [message, setMessage] = useState('')
    const [selected, setSelected] = useState<MobileOfferAssignment | null>(null)
    const [revoking, setRevoking] = useState(false)
    const [reassigning, setReassigning] = useState<MobileOfferAssignment | null>(null)
    const sequence = useRef(0)
    const invalidateRequests = useCallback(() => {
        sequence.current++
    }, [])
    const revokeInFlight = useRef(false)
    const active = useRef(true)
    const load = useCallback(
        async (cursor?: string) => {
            const request = ++sequence.current
            setLoading(true)
            setError('')
            try {
                const page = await mobileOffersApi.history(userId, cursor)
                if (request !== sequence.current || !active.current) return
                setRecords((current) => (cursor ? [...current, ...page.items.filter((record) => !current.some((item) => item.id === record.id))] : page.items))
                setNextCursor(page.nextCursor)
                page.items.forEach(onStatusChanged)
            } catch (failure) {
                if (request === sequence.current && active.current) setError(mobileOfferError(failure))
            } finally {
                if (request === sequence.current && active.current) setLoading(false)
            }
        },
        [userId, onStatusChanged]
    )

    useEffect(() => {
        active.current = true
        return () => {
            active.current = false
            invalidateRequests()
        }
    }, [invalidateRequests])
    useEffect(() => {
        void load()
    }, [load, refreshToken])

    const revoke = async () => {
        if (!selected || revokeInFlight.current) return
        revokeInFlight.current = true
        setRevoking(true)
        setError('')
        setMessage('')
        let confirmed: MobileOfferAssignment | undefined
        try {
            const response = await mobileOffersApi.revoke(selected.id, selected.revision ?? 0)
            if (response.revoked) confirmed = { ...selected, status: MobileOfferAssignmentStatus.REVOKED }
        } catch (failure) {
            try {
                const observed = await findCustomerAssignment(mobileOffersApi.history, userId, (record) => record.id === selected.id)
                if (observed?.status === MobileOfferAssignmentStatus.REVOKED && (observed.revision ?? 0) === (selected.revision ?? 0)) confirmed = observed
                else if (observed && active.current) onStatusChanged(observed)
            } catch {
                /* A failed read leaves the mutation outcome uncertain. */
            }
            if (!confirmed && active.current) setError(`${mobileOfferError(failure)} Revocation is not confirmed. Refresh history before retrying.`)
        }
        revokeInFlight.current = false
        if (!active.current) return
        setRevoking(false)
        setSelected(null)
        if (confirmed) {
            const record = confirmed
            setRecords((current) => current.map((item) => (item.id === record.id ? record : item)))
            onStatusChanged(record)
            setMessage('Assignment revoked. No subscription was canceled. An Apple code already disclosed may still be redeemable.')
            void load()
        } else {
            setError((current) => current || 'Revocation is not confirmed. Refresh customer history before retrying.')
        }
    }

    return (
        <div id="customer-assignment-history" className="scroll-mt-4">
            <OfferSection title="Assignment history">
                <div className="flex flex-wrap items-center justify-between gap-3">
                    <p className={customerOfferSecondaryClass}>Assignment events for this account, not its current subscription status.</p>
                    <OfferButton variant="secondary" disabled={loading || revoking} onClick={() => void load()}>
                        <RefreshCw className="h-4 w-4" />
                        Refresh history
                    </OfferButton>
                </div>
                {message && <OfferNotice>{message}</OfferNotice>}
                {error && <OfferNotice error>{error}</OfferNotice>}
                {loading && <OfferNotice>Loading assignment history…</OfferNotice>}
                {!loading && !error && !records.length && (
                    <div className="flex flex-col items-center gap-3 py-8 text-center">
                        <History className="h-7 w-7 text-slate-400 dark:text-slate-500" />
                        <h3 className="font-medium">No assignments yet</h3>
                        <p className={customerOfferSecondaryClass}>Review and confirm an offer to create the first assignment.</p>
                    </div>
                )}
                {!!records.length && (
                    <div className="overflow-x-auto">
                        <table className="w-full text-left text-sm">
                            <thead className="border-b border-slate-200 text-xs text-slate-500 dark:border-slate-700 dark:text-slate-400">
                                <tr>
                                    {['Offer · reason', 'Store', 'Assigned', 'Redeem before', 'Status', 'Operator', 'Action'].map((label) => (
                                        <th key={label} className="px-3 py-3">
                                            {label}
                                        </th>
                                    ))}
                                </tr>
                            </thead>
                            <tbody className="divide-y divide-slate-200 dark:divide-slate-700">
                                {records.map((record) => (
                                    <tr key={record.id}>
                                        <td className="min-w-48 px-3 py-4">
                                            <p className="font-medium">
                                                {customerPlanName(record.plan)} · {record.environment === 'sandbox' ? 'Test' : 'Real'}
                                            </p>
                                            <p className="mt-1 max-w-xs whitespace-pre-wrap break-words text-xs text-slate-500 dark:text-slate-400">
                                                {record.reason}
                                            </p>
                                            <p className="mt-1 text-xs text-slate-500 dark:text-slate-400">Offer {record.offerId}</p>
                                            <p className="mt-1 text-xs text-slate-500 dark:text-slate-400">Revision {record.revision ?? 0}</p>
                                            {!!record.previousAssignments?.length && (
                                                <details className="mt-3 text-xs">
                                                    <summary className="cursor-pointer font-medium">
                                                        Previous assignments ({record.previousAssignments.length})
                                                    </summary>
                                                    <ol className="mt-2 space-y-3">
                                                        {record.previousAssignments.map((previous) => (
                                                            <li key={previous.revision ?? 0} className="space-y-1">
                                                                <p className="font-medium">
                                                                    Revision {previous.revision ?? 0} · {previous.status}
                                                                </p>
                                                                <p className="whitespace-pre-wrap break-words">{previous.reason}</p>
                                                                <p>
                                                                    Assigned {customerOfferDate(previous.assignedAt)} by {previous.assignedBy}
                                                                </p>
                                                                <p>Redeem before {customerOfferDate(previous.redeemBy)}</p>
                                                                {previous.revokedAt && (
                                                                    <p>
                                                                        Revoked {customerOfferDate(previous.revokedAt)} by {previous.revokedBy}
                                                                    </p>
                                                                )}
                                                                <p>Superseded {customerOfferDate(previous.supersededAt)}</p>
                                                            </li>
                                                        ))}
                                                    </ol>
                                                </details>
                                            )}
                                        </td>
                                        <td className="px-3 py-4 whitespace-nowrap">{customerStoreName(record.provider)}</td>
                                        <td className="min-w-36 px-3 py-4">{customerOfferDate(record.assignedAt)}</td>
                                        <td className="min-w-36 px-3 py-4">{customerOfferDate(record.redeemBy)}</td>
                                        <td className="px-3 py-4">
                                            <span className="rounded-full bg-slate-100 px-2 py-1 text-xs font-medium capitalize text-slate-700 dark:bg-slate-800 dark:text-slate-300">
                                                {record.status}
                                            </span>
                                        </td>
                                        <td className="px-3 py-4 text-xs">{record.assignedBy}</td>
                                        <td className="px-3 py-4">
                                            {record.provider === 'apple' &&
                                            record.status !== MobileOfferAssignmentStatus.REDEEMED &&
                                            (canReassignOffer(record) || hasPendingReassignment(record)) ? (
                                                <OfferButton
                                                    variant="ghost"
                                                    disabled={loading || revoking}
                                                    onClick={() => {
                                                        setReassigning(record)
                                                        setError('')
                                                        setMessage('')
                                                    }}
                                                >
                                                    {hasPendingReassignment(record) ? 'Resume reassignment' : 'Reassign offer'}
                                                </OfferButton>
                                            ) : record.status === MobileOfferAssignmentStatus.ASSIGNED ? (
                                                <OfferButton
                                                    variant="ghost"
                                                    disabled={loading || revoking}
                                                    onClick={() => {
                                                        setSelected(record)
                                                        setError('')
                                                    }}
                                                >
                                                    Revoke
                                                </OfferButton>
                                            ) : (
                                                '—'
                                            )}
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                )}
                {nextCursor && (
                    <OfferButton variant="secondary" disabled={loading || revoking} onClick={() => void load(nextCursor)}>
                        Load older assignments
                    </OfferButton>
                )}
                {selected && (
                    <CustomerActionDialog title="Revoke this assignment?" busy={revoking} onClose={() => setSelected(null)}>
                        <div className="space-y-5">
                            <p className="text-sm">
                                {customerPlanName(selected.plan)} · {customerStoreName(selected.provider)} · Offer {selected.offerId}
                            </p>
                            <OfferNotice>
                                Revoking an unused assignment does not cancel an existing subscription. Apple codes already disclosed may remain redeemable.
                            </OfferNotice>
                            <div className="flex flex-wrap justify-end gap-3">
                                <OfferButton variant="secondary" disabled={revoking} onClick={() => setSelected(null)}>
                                    Keep assignment
                                </OfferButton>
                                <OfferButton variant="danger" disabled={revoking} onClick={() => void revoke()}>
                                    {revoking ? 'Confirming…' : 'Revoke assignment'}
                                </OfferButton>
                            </div>
                        </div>
                    </CustomerActionDialog>
                )}
                {reassigning && (
                    <CustomerReassignmentDialog
                        key={`${reassigning.id}:${reassigning.revision ?? 0}`}
                        assignment={reassigning}
                        onClose={() => {
                            setReassigning(null)
                            void load()
                        }}
                        onResult={(record, deliveryConfirmed) => {
                            invalidateRequests()
                            setLoading(false)
                            setRecords((current) =>
                                current.map((item) => (item.id === record.id && (record.revision ?? 0) >= (item.revision ?? 0) ? record : item))
                            )
                            onReassigned({ assignment: record, deliveryConfirmed })
                            setMessage(
                                deliveryConfirmed
                                    ? 'Offer reassigned. Previous assignment terms remain in history.'
                                    : 'Reassignment saved. Code delivery is not confirmed.'
                            )
                        }}
                    />
                )}
            </OfferSection>
        </div>
    )
}
